import React, { useEffect } from 'react';
import { Tooltip, IconButton, Box, useTheme, Menu, Dialog, DialogTitle, Typography, DialogContent, Button, MenuItem, ListItemIcon, ListItemText, ListSubheader, Divider } from '@mui/material';
import { Stack, SxProps } from '@mui/system';
import { Visibility, OpenInFull, MoreVert, OpenWith, ContentCopy, DeleteForever, Settings, Search, Refresh, CameraAlt, CloudDownload, ContentPaste, PictureAsPdf, ContentCopyTwoTone} from '@mui/icons-material';
import { WidgetBaseProps } from "components/widgetParts/WidgetContainer";
import { useTranslation } from 'react-i18next';
import { useDashboard } from "routes/dashboard/Dashboard";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from 'react';
import { currentUser, permissionTest } from 'system/InfegyUser';
import WidgetExportsDialog from './exports/WidgetExportsDialog';
import WidgetImageDownloadDialog from './exports/WidgetImageDownloadDialog';
import WidgetPDFDownloadDialog from './exports/WidgetPDFDownloadDialog';
import { ActiveWidgetRequest, WidgetQuery, WidgetRequestInfo } from 'system/widget/Widget';
import { enqueueSnackbar } from 'notistack';
import { APIError, apiRequestCacheExpiration } from 'system/APIRequest';
import { PDFExportOptions } from 'utils/usePDFExport';
import { useWidgetInteraction } from 'routes/dashboard/DashboardRoot';

interface WidgetOverlayProps extends WidgetBaseProps {
  // Include a button to open a query dialog
  hasQueryDialog?: boolean,
  // Include a button to open a settings dialog
  hasSettingsDialog?: boolean,
  // Optionally hide the export data option
  hideDataExports?: boolean,
  // Optionally hide the default option to Download an image of the widget
  hideImageExport?: boolean,
  // Optionally show the default option to Download a PDF of the widget
  showPDFExport?: boolean,
  // Active requests used to build this widget if any
  activeRequestInfos?: any,
  // Array of export amounts to show in the export dialog, or undefined/empty for no selector
  exportAmounts?: string[],

  onQueryDialogOpened?: () => void,
  onSettingsOpened?: () => void,
  onDownloadPDF?: (options?: PDFExportOptions) => Promise<void>;

  saveWidgetQueries?: (widgetQueries: WidgetQuery[] | undefined) => Promise<void>;
  activeWidgetRequests?: Array<ActiveWidgetRequest | ActiveWidgetRequest[]>;
  widgetRef?: React.RefObject<HTMLDivElement> | null;
}

interface CopiedWidgetQueries {
  sourceWidgetId: string;
  widgetQueries: WidgetQuery[];
}

interface CopiedWidgetSettings {
  sourceWidgetId: string;
  sourceWidgetType: string;
  widgetData: any;
  widgetRequestInfo: WidgetRequestInfo | undefined;
}

export function WidgetOverlay(props: WidgetOverlayProps): JSX.Element {
  const theme = useTheme();
  const widgetInteractionInfo = useWidgetInteraction();

  const {i18n} = useTranslation();
  const dashboard = useDashboard();
  const user = currentUser();

  const [showCopyWarning, setShowCopyWarning] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDownloadImageDialog, setShowDownloadImageDialog] = useState(false);
  const [showDownloadPDFDialog, setShowDownloadPDFDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuIsOpen = Boolean(menuAnchorEl);

  // Fetch copied widget queries from local storage
  const copiedWidgetQueries: CopiedWidgetQueries | null = JSON.parse(localStorage.getItem('copiedWidgetQueries') || 'null');
  const hasCopiedWidgetQueries = copiedWidgetQueries && copiedWidgetQueries?.sourceWidgetId !== props.widget?.id && props.saveWidgetQueries;

  useEffect(() => {
    if (widgetInteractionInfo?.newWidgetID === props.widget?.id) {
      widgetInteractionInfo.setNewWidgetID?.(undefined);
      props.onSettingsOpened && props.onSettingsOpened();
    }
  }, [widgetInteractionInfo?.newWidgetID]);

  function CopyWidgetQueries() {
    if (props.widget) {
      const dataToCopy: CopiedWidgetQueries = {
        sourceWidgetId: props.widget.id,
        widgetQueries: props.widget.request_info?.queries || [],
      };
      localStorage.setItem('copiedWidgetQueries', JSON.stringify(dataToCopy));
    }
  }
  function PasteWidgetQueries() {
    if (hasCopiedWidgetQueries && props.saveWidgetQueries) {
      try {
        props.saveWidgetQueries(copiedWidgetQueries?.widgetQueries);
      } catch (err: any) {
        enqueueSnackbar(`${err.message}: ${err.server_message}`, {variant: 'error', persist: true});
      }
      // props.saveWidgetQueries(copiedWidgetQueries?.widgetQueries).then(() => {
      //   if (props.widgetRefresh) {
      //     props.widgetRefresh();
      //   }
      // }).catch((err: APIError) => {
      //   enqueueSnackbar(`${err.message}: ${err.server_message}`, {variant: 'error', persist: true});
      // });
    }
  }

  // Fetch copied widget data from local storage
  const copiedWidgetSettings: CopiedWidgetSettings | null = JSON.parse(localStorage.getItem('copiedWidgetSettings') || 'null');
  const hasCopiedWidgetSettings = copiedWidgetSettings && copiedWidgetSettings.sourceWidgetId !== props.widget.id &&
    copiedWidgetSettings.widgetData && copiedWidgetSettings.sourceWidgetType === props.widget.name;
  function CopyWidgetSettings() {
    if (props.widget) {
      const dataToCopy: CopiedWidgetSettings = {
        sourceWidgetId: props.widget.id,
        sourceWidgetType: props.widget.name,
        widgetData: props.widget.data,
        widgetRequestInfo: props.widget.request_info,
      };
      localStorage.setItem('copiedWidgetSettings', JSON.stringify(dataToCopy));
    }
  }
  function PasteWidgetSettings() {
    if (hasCopiedWidgetSettings) {
      props.onUpdateWidget(props.widget.id, {
        data: copiedWidgetSettings?.widgetData,
        request_info: copiedWidgetSettings?.widgetRequestInfo,
      }, true).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      }).catch((err: APIError) => {
        enqueueSnackbar(`${err.message}: ${err.server_message}`, {variant: 'error', persist: true});
      });
    }
  }

  const showDataExports = permissionTest("export_data") && props.activeWidgetRequests?.length && !props.hideDataExports;

  const cornerOverlaySX: SxProps = {
    position: "absolute",
    top: "0",
    right: "0",
    borderRadius: "10px",
    width: "auto",
    height: "auto",
    padding: "5px",
    opacity: "0",
    transition: "all .15s",
    "&:hover, &:active": {
      backdropFilter:"blur(12px) contrast(90%) grayscale(20%)",
      opacity: "1"
    },
  };

  const overlaySX: SxProps = {
    ...cornerOverlaySX,
    left: "0",
    bottom: "0",
    width: "100%",
    height: "100%",
  };

  return (
  <>
      {props.isInLayoutMode &&
        <Box sx={overlaySX} className="react-grid-item-overlay" >
          <OpenInFull
            fontSize="small"
            sx={{
              position:"absolute",
              bottom:"5px", right:"5px",
              transform:"rotate(90deg)", opacity:0.5 }}>
          </OpenInFull>
          {props?.widget?.name &&
            <Box sx={{position:"absolute", bottom:"5px", left:"50%", transform:"translateX(-50%)", mouseEvents:"none", pointerEvents:"none", opacity:0.5}}>
              <Typography variant="body1">
                {i18n.t(`widget-types.${props.widget.name}.label`)}
              </Typography>
            </Box>
          }
        </Box>
      }
      <Box sx={cornerOverlaySX} className="drag-cancel react-grid-item-overlay-corner" >
        {dashboard?._modifiable && (<>
          {props.hasQueryDialog &&
            <Tooltip followCursor title={i18n.t("dashboard.open-query-form")}>
              <IconButton
                size='small'
                aria-label={i18n.t("dashboard.open-query-form")}
                onClick={() => {
                  props.onQueryDialogOpened && props.onQueryDialogOpened();
                }}>
                <Search/>
              </IconButton>
            </Tooltip>
          }
          {props.hasSettingsDialog &&
            <Tooltip followCursor title={i18n.t("dashboard.open-widget-settings")}>
              <IconButton
                size='small'
                aria-label={i18n.t("dashboard.open-widget-settings")}
                onClick={() => {
                  props.onSettingsOpened && props.onSettingsOpened();
                }}>
                <Settings/>
              </IconButton>
            </Tooltip>
          }
        </>)}
        <Tooltip followCursor title={i18n.t("dashboard.widget-options-menu")}>
          <IconButton
            size='small'
            aria-label={i18n.t("dashboard.widget-options-menu")}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              setMenuAnchorEl(event.currentTarget);
            }}>
            <MoreVert/>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        className="drag-cancel"
        anchorEl={menuAnchorEl}
        open={menuIsOpen}
        sx={{p:10}}
        onClose={() => setMenuAnchorEl(null)}>
        <ListSubheader sx={{fontWeight: 'bold', lineHeight: '1.4', fontSize: '1rem', p:0, m: 0, mt: "-8px", mb: "5px", textAlign: 'center', opacity: 0.8}}>
          {i18n.t(`widget-types.${props.widgetRegistryInfo?.id}.label`)}
        </ListSubheader>
        {dashboard?._modifiable &&
          <>
            {props.hasQueryDialog &&
              <MenuItem onClick={() => {
                props.onQueryDialogOpened && props.onQueryDialogOpened();
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><Search/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.open-query-form")}</ListItemText>
              </MenuItem>
            }
            {props.hasSettingsDialog &&
              <MenuItem onClick={() => {
                props.onSettingsOpened && props.onSettingsOpened();
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><Settings/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.open-widget-settings")}</ListItemText>
              </MenuItem>
            }
          </>
        }
        {(dashboard?._modifiable && !dashboard.template_type) && (
          <>
            <MenuItem onClick={() => {
              props.onUpdateWidget(props.widget.id, {cache: null}, true);
              enqueueSnackbar({message: i18n.t("widgets.reload-widget-success", {minutes: Math.ceil(apiRequestCacheExpiration/(60*1000))}), variant: "info"});
              setMenuAnchorEl(null);
            }}>
              <ListItemIcon><Refresh/></ListItemIcon>
              <ListItemText>{i18n.t("widgets.reload-widget")}</ListItemText>
            </MenuItem>
          </>
        )}
        {!props.hideImageExport &&
          <MenuItem onClick={() => {
            setShowDownloadImageDialog(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><CameraAlt/></ListItemIcon>
            <ListItemText>{i18n.t("download")} {i18n.t("image")}</ListItemText>
          </MenuItem>
        }
        {props.showPDFExport &&
          <MenuItem onClick={() => {
            setShowDownloadPDFDialog(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><PictureAsPdf/></ListItemIcon>
            <ListItemText>{i18n.t("widgets.download-pdf-dialog")}</ListItemText>
          </MenuItem>
        }
        {!!showDataExports &&
          <MenuItem onClick={() => {
            setShowExportDialog(true);
            setMenuAnchorEl(null);
          }}>
            <ListItemIcon><CloudDownload/></ListItemIcon>
            <ListItemText>{i18n.t("widgets.export-options-dialog")}</ListItemText>
          </MenuItem>
        }
        {dashboard?._modifiable &&
          <>
            <MenuItem onClick={() => {
              const uncopyableWidgets = ["choropleth", "heatmap"];
              if (uncopyableWidgets.includes(props.widget.name)) {
                setShowCopyWarning(true);
                return;
              }
              if (props.onCopyWidget) {
                props.onCopyWidget(props.widget);
                props?.setIsInLayoutMode && props.setIsInLayoutMode(true);
              }
              setMenuAnchorEl(null);
            }}>
              <ListItemIcon><ContentCopyTwoTone/></ListItemIcon>
              <ListItemText>{i18n.t("duplicate")} {i18n.t("widget")}</ListItemText>
            </MenuItem>
          </>
        }
        {dashboard?._modifiable &&
          <>
            {props.isInLayoutMode &&
              <MenuItem onClick={() => {
                props?.setIsInLayoutMode && props.setIsInLayoutMode(false);
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><Visibility/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.leave-layout-mode")}</ListItemText>
              </MenuItem>
            }
            {!props.isInLayoutMode &&
              <MenuItem onClick={() => {
                props?.setIsInLayoutMode && props.setIsInLayoutMode(true);
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><OpenWith/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.layout-mode")}</ListItemText>
              </MenuItem>
            }
          </>
        }

        {user?.role === 'analyst' &&
          <>
            <Divider />
            {(props.widget.request_info?.queries && (props.widget.request_info?.queries?.length || props.widget.request_info?.requests?.length)) && (
              <MenuItem onClick={() => {
                CopyWidgetQueries();
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><ContentCopy/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.copy-widget-queries")}</ListItemText>
              </MenuItem>
            )}
            {(dashboard?._modifiable && hasCopiedWidgetQueries) && (
              <MenuItem onClick={() => {
                PasteWidgetQueries();
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><ContentPaste/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.paste-widget-queries")}</ListItemText>
              </MenuItem>
            )}
            {
              <MenuItem onClick={() => {
                CopyWidgetSettings();
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><ContentCopy/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.copy-widget-settings")}</ListItemText>
              </MenuItem>
            }
            {(dashboard?._modifiable && hasCopiedWidgetSettings) && (
              <MenuItem onClick={() => {
                PasteWidgetSettings();
                setMenuAnchorEl(null);
              }}>
                <ListItemIcon><ContentPaste/></ListItemIcon>
                <ListItemText>{i18n.t("dashboard.paste-widget-settings")}</ListItemText>
              </MenuItem>
            )}
          </>
        }
        {dashboard?._modifiable &&
          <>
            <Divider />
            <MenuItem onClick={() => {
              props.onDeleteWidget && props.onDeleteWidget(props.widget?.id);
              setMenuAnchorEl(null);
            }}>
              <ListItemIcon><DeleteForever sx={{ color: theme.palette.error.main }}/></ListItemIcon>
              <ListItemText>{i18n.t("delete")}</ListItemText>
            </MenuItem>
          </>
        }
      </Menu>
      <Dialog open={showCopyWarning}>
        <DialogTitle>
          <Typography variant="h3">{i18n.t("dashboard.copy-unique-widget-title")}</Typography>
        </DialogTitle>
        <IconButton onClick={() => setShowCopyWarning(false)} sx={{position: "absolute", right: 8, top: 8}}>
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{minWidth: 400}}>
          <Stack spacing={1}>
            <Typography variant="body1">{i18n.t("dashboard.copy-unique-widget-text")}</Typography>
            <Button sx={{display: "block", alignSelf: "end", width: 100}} variant="contained" color="primary" onClick={() => setShowCopyWarning(false)} >Okay</Button>
          </Stack>
        </DialogContent>
      </Dialog>
      { showExportDialog &&
        <WidgetExportsDialog
          {...props as WidgetBaseProps}
          onClose={() => setShowExportDialog(false)}>
        </WidgetExportsDialog>
      }
      { showDownloadImageDialog &&
        <WidgetImageDownloadDialog
          {...props as WidgetBaseProps}
          onClose={() => setShowDownloadImageDialog(false)}>
        </WidgetImageDownloadDialog>
      }
      { showDownloadPDFDialog &&
        <WidgetPDFDownloadDialog
          defaultFilename={props.widget?.name}
          onDownloadPDF={props.onDownloadPDF}
          onClose={() => setShowDownloadPDFDialog(false)}>
        </WidgetPDFDownloadDialog>
      } 
  </>
);
}